import React, { useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_PURCHASE_ORDER,
  UPDATE_PURCHASE_ORDER,
  CANCEL_PURCHASE_ORDER,
} from "./queries";
import { PurchaseOrderItem, PO_STATUS_LABELS } from "./models";

type Params = { id: string };

const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<Params>();

  const [fetchOrder, { data, loading }] = useLazyQuery(GET_PURCHASE_ORDER, {
    fetchPolicy: "no-cache",
  });
  const [updateOrder] = useMutation(UPDATE_PURCHASE_ORDER);
  const [cancelOrder] = useMutation(CANCEL_PURCHASE_ORDER);

  useEffect(() => {
    fetchOrder({ variables: { id } });
    //eslint-disable-next-line
  }, [id]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  const handleSendToSupplier = async () => {
    try {
      await updateOrder({ variables: { id, status: "sent" } });
      toast.success(lang("Orden enviada", "Order sent"));
      fetchOrder({ variables: { id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(lang("¿Cancelar esta orden?", "Cancel this order?"))) {
      return;
    }
    try {
      await cancelOrder({ variables: { id } });
      toast.success(lang("Orden cancelada", "Order cancelled"));
      fetchOrder({ variables: { id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (loading) return <LoaderDots fullSize />;
  if (!data?.getPurchaseOrder) return null;

  const po = data.getPurchaseOrder;
  const statusInfo = PO_STATUS_LABELS[po.status] || {
    label: po.status,
    labelEn: po.status,
    color: "secondary",
  };
  const canReceive = po.status === "sent" || po.status === "partial";
  const canSend = po.status === "draft";
  const canCancel =
    po.status === "draft" || po.status === "sent" || po.status === "partial";

  return (
    <>
      <p>
        <Link to="/inventory/purchases">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Órdenes de Compra", "Purchase Orders")}
        </Link>
      </p>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">
          OC-{po.id}{" "}
          <span className={`badge badge-${statusInfo.color} ml-2`}>
            {lang(statusInfo.label, statusInfo.labelEn)}
          </span>
        </h1>
        <div>
          {canSend && (
            <button
              className="btn btn-primary mr-2"
              onClick={handleSendToSupplier}
            >
              <i className="fas fa-paper-plane"></i>{" "}
              {lang("Enviar a Proveedor", "Send to Supplier")}
            </button>
          )}
          {canReceive && (
            <Link
              to={`/inventory/purchases/${id}/receive`}
              className="btn btn-success mr-2"
            >
              <i className="fas fa-truck"></i>{" "}
              {lang("Recibir Entrega", "Receive Delivery")}
            </Link>
          )}
          {canCancel && (
            <button className="btn btn-outline-danger" onClick={handleCancel}>
              <i className="fa fa-times"></i> {lang("Cancelar", "Cancel")}
            </button>
          )}
        </div>
      </div>

      <div className="row">
        {/* PO info */}
        <div className="col-lg-4">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Información", "Information")}</h4>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">
                    {lang("Proveedor", "Supplier")}
                  </td>
                  <td className="font-weight-bold">
                    {po.supplier?.name || "—"}
                  </td>
                </tr>
                {po.supplier?.contact_name && (
                  <tr>
                    <td className="text-muted">
                      {lang("Contacto", "Contact")}
                    </td>
                    <td>{po.supplier.contact_name}</td>
                  </tr>
                )}
                {po.supplier?.phone && (
                  <tr>
                    <td className="text-muted">
                      {lang("Teléfono", "Phone")}
                    </td>
                    <td>
                      <a href={`tel:${po.supplier.phone}`}>
                        {po.supplier.phone}
                      </a>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted">
                    {lang("Sucursal", "Branch")}
                  </td>
                  <td>{po.branch?.name || "—"}</td>
                </tr>
                <tr>
                  <td className="text-muted">
                    {lang("Creada", "Created")}
                  </td>
                  <td>{formatDate(po.created_at)}</td>
                </tr>
                <tr>
                  <td className="text-muted">
                    {lang("Entrega esperada", "Expected")}
                  </td>
                  <td>{formatDate(po.expected_date)}</td>
                </tr>
                {po.received_date && (
                  <tr>
                    <td className="text-muted">
                      {lang("Recibida", "Received")}
                    </td>
                    <td>{formatDate(po.received_date)}</td>
                  </tr>
                )}
                {po.notes && (
                  <tr>
                    <td className="text-muted">{lang("Notas", "Notes")}</td>
                    <td>{po.notes}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="material_card p-3 mb-3">
            <h4>{lang("Totales", "Totals")}</h4>
            <table className="table table-sm mb-0">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td className="text-right">
                    ${Number(po.subtotal || 0).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td>IVA</td>
                  <td className="text-right">
                    ${Number(po.tax || 0).toFixed(2)}
                  </td>
                </tr>
                <tr className="font-weight-bold">
                  <td>Total</td>
                  <td className="text-right">
                    ${Number(po.total || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Items */}
        <div className="col-lg-8">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Productos", "Items")}</h4>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="thead-light">
                  <tr>
                    <th>{lang("Ingrediente", "Ingredient")}</th>
                    <th className="text-center">
                      {lang("Ordenado", "Ordered")}
                    </th>
                    <th className="text-center">
                      {lang("Recibido", "Received")}
                    </th>
                    <th className="text-center">
                      {lang("Dañado", "Damaged")}
                    </th>
                    <th>{lang("Unidad", "Unit")}</th>
                    <th className="text-right">
                      {lang("Precio/U", "Price/U")}
                    </th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {po.items.map((item: PurchaseOrderItem) => {
                    const received = item.quantity_received || 0;
                    const ordered = item.quantity_ordered;
                    const pct =
                      ordered > 0 ? (received / ordered) * 100 : 0;

                    return (
                      <tr key={item.id}>
                        <td className="font-weight-bold">
                          {item.ingredient?.name || `#${item.ingredient_id}`}
                        </td>
                        <td className="text-center">
                          {Number(ordered).toFixed(2)}
                        </td>
                        <td className="text-center">
                          <span
                            className={
                              pct >= 100
                                ? "text-success font-weight-bold"
                                : pct > 0
                                ? "text-warning font-weight-bold"
                                : ""
                            }
                          >
                            {Number(received).toFixed(2)}
                          </span>
                        </td>
                        <td className="text-center">
                          {item.quantity_damaged
                            ? (
                                <span className="text-danger">
                                  {Number(item.quantity_damaged).toFixed(2)}
                                </span>
                              )
                            : "—"}
                        </td>
                        <td>{item.unit}</td>
                        <td className="text-right">
                          ${Number(item.unit_price).toFixed(2)}
                        </td>
                        <td className="text-right">
                          ${Number(item.total).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderDetail;
